import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type EnhancementGalleryImage = { id: string; url: string };

export type StayEnhancement = {
  id: string;
  slug: string;
  label: string;
  cover_image_url: string | null;
  description: string | null;
  gallery: EnhancementGalleryImage[];
  position: number;
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const COLS = "id,slug,label,cover_image_url,description,gallery,position";

function normalize(rows: Array<Omit<StayEnhancement, "gallery"> & { gallery: unknown }>): StayEnhancement[] {
  return rows.map((row) => ({
    ...row,
    gallery: Array.isArray(row.gallery)
      ? (row.gallery as unknown[])
          .map((v): EnhancementGalleryImage | null => {
            if (
              v &&
              typeof v === "object" &&
              typeof (v as { id?: unknown }).id === "string" &&
              typeof (v as { url?: unknown }).url === "string"
            ) {
              return {
                id: (v as { id: string }).id,
                url: (v as { url: string }).url,
              };
            }
            return null;
          })
          .filter((v): v is EnhancementGalleryImage => v !== null)
      : [],
  }));
}

export const listStayEnhancements = createServerFn({ method: "GET" }).handler(
  async (): Promise<StayEnhancement[]> => {
    const sb = publicClient();
    const { data, error } = await sb
      .from("stay_enhancements")
      .select(COLS)
      .order("position", { ascending: true });
    if (error) throw new Error(error.message);
    return normalize((data ?? []) as Array<Omit<StayEnhancement, "gallery"> & { gallery: unknown }>);
  },
);

const itemSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers, and dashes only"),
  label: z.string().min(1).max(120),
  cover_image_url: z.string().nullable(),
  description: z.string().nullable(),
  gallery: z
    .array(z.object({ id: z.string().uuid(), url: z.string().url() }))
    .max(40),
});

export const upsertStayEnhancement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => itemSchema.parse(data))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    if (data.id) {
      const { error } = await sb
        .from("stay_enhancements")
        .update({
          slug: data.slug,
          label: data.label,
          cover_image_url: data.cover_image_url,
          description: data.description,
          gallery: data.gallery,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: existing } = await sb
      .from("stay_enhancements")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = (existing?.[0]?.position ?? -1) + 1;
    const { data: row, error } = await sb
      .from("stay_enhancements")
      .insert({
        slug: data.slug,
        label: data.label,
        cover_image_url: data.cover_image_url,
        description: data.description,
        gallery: data.gallery,
        position: nextPos,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteStayEnhancement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("stay_enhancements")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderStayEnhancements = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ ordered_ids: z.array(z.string().uuid()) }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("stay_enhancements")
        .update({ position: i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });
